import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getProductsList } from "../../api/services";
import "./Search.css";

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Helper: get the lowest final_price from product variants
  const getMinPrice = (variants) => {
    if (!variants || variants.length === 0) return null;
    const prices = variants.map((v) => v.final_price);
    return Math.min(...prices);
  };

  // Fetch search suggestions from your API
  const fetchSuggestions = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        _search: encodeURIComponent(searchTerm),
        _page: 1,
        _perPage: 20,
      };

      const response = await getProductsList(params);
      setSuggestions(response?.data?.data?.products);
    } catch (error) {
      console.error("Search API error:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) {
        fetchSuggestions(query);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, fetchSuggestions]);

  // Hide dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (product) => {
    // Navigate to product detail page using the slug
    navigate(`/product/${product.slug}`);
    setQuery("");
    setShowDropdown(false);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length,
      );
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedIndex]);
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className="search-input-wrapper">
        <input
          type="text"
          className="search-input form-control"
          placeholder="Search..."
          autoComplete="off"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setShowDropdown(true)}
          required
        />
        <div className="search-button">
          <i className="fas fa-search"></i>
        </div>
      </div>

      {/* Autocomplete dropdown */}
      {showDropdown && (
        <ul className="search-autocomplete-dropdown">
          {loading && <li className="loading-item">Loading...</li>}
          {!loading && suggestions.length === 0 && query && (
            <li className="no-results">No products found</li>
          )}
          {!loading &&
            suggestions.map((product, idx) => {
              const minPrice = getMinPrice(product.product_variants);
              return (
                <li
                  key={product.id}
                  onClick={() => handleSuggestionClick(product)}
                  className={idx === selectedIndex ? "active" : ""}
                >
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.name}
                      width="40"
                      height="40"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  )}
                  <div className="suggestion-details">
                    <span className="product-name">{product.name}</span>
                    <span className="product-category">
                      {product.category_name}
                    </span>
                    {minPrice && (
                      <span className="product-price">
                        ₹{minPrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
      )}
    </form>
  );
};

export default SearchBar;
